import React, {RefObject, useEffect, useState} from "react";
import cv from "@techstark/opencv-js";
import '../../../assets/styles/tools/imageRemoveBg.css';
import '../../../assets/styles/App.css';
import {Tooltip, Dropdown} from "antd";
import type { MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import {Brush, ImageDown, ImageUp, PaintBucket, RefreshCw} from "lucide-react";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {createInferenceSession, imageBgRemove, modelIsLoaded, recreateInferenceSession} from "./imageBgRemove";
import {useImageEditorInfoStore} from "../../../store/imageEditorInfoStore";
import {ImageEditor} from "./imageEditor";
import {CubeGridLoader} from "../../loading/CubeGridLoader";
import {modelExists, setModelIndex} from "../../../utils/model";
import {ModelTypeConstant, RemoveBgModelList} from "../../../constants";
import {basicDownloadImage} from "../../../utils";
import {useNotificationMessage} from "../../../hooks/NotificationContext";

interface IImageRemoveBgProps {
    isLight: boolean
}

const sliderInitPosition = 150;

const demoSourceImageUrl = `/images/20240323183659590520.png`;// 演示原图像
const demoResultImageUrl = `/images/processed_image.png`;// 演示输出图像
const maxWidth = 800;
const maxHeight = 500;

const imageHeight = '350px'; // 图片高度
const modelSelectInfo = [
    {
        label: 'General background remover Lite',
        key: '0',
    },
    {
        label: 'General background remover',
        key: '1',
    },
    {
        label: 'General background remover Pro',
        key: '2',
    },
    {
        label: 'General background remover Pro Max',
        key: '3',
    },
    {
        label: 'Human background remover',
        key: '4',
    },
    {
        label: 'Animation background remover',
        key: '5',
    },
    // {
    //     label: 'isnet standard model',
    //     key: '6',
    // },
    // {
    //     label: 'isnet_model_fp16',
    //     key: '7',
    // },
    // {
    //     label: 'isnet_carve_photo',
    //     key: '8',
    // },
];
const modelSelectItems: MenuProps['items'] = modelSelectInfo;
export const ImageRemoveBg: React.FC<IImageRemoveBgProps> = ({isLight}) => {
    // 消息栏
    const {showAlert} = useAlertMessage();
    const { showLoadingNotification } = useNotificationMessage();

    // 状态管理
    const [sliderPosition, setSliderPosition] = useState(sliderInitPosition);// 滑块当前位置
    const [separator, setSeparator] = useState<HTMLDivElement>()

    const [modelSelectKey, setModelSelectKey] = useState({
        key: 0,
        label: modelSelectInfo[0].label
    });

    const [isShowEditor, setIsShowEditor] = useState(false);
    const [isAllowEdit, setIsAllowEdit] = useState(false);

    const [isLoading, setIsLoading] = useState(false);// 是否正在处理
    const [isModelLoading, setIsModelLoading] = useState(false);// 是否正在加载模型

    // 原图像和输出图像
    const {originalCanvasBase64, finalCanvasBase64 , setOriginalCanvasBase64, setFinalCanvasBase64} = useImageEditorInfoStore();

    const containerRef = React.useRef<HTMLDivElement>(null);// 展示图片container ref
    const fileInputRef = React.createRef<HTMLInputElement>();// 文件上传ref

    cv["onRuntimeInitialized"] = async () => {// 异步初始化onnxruntime
        setIsModelLoading(true);
        recreateInferenceSession(showAlert, showLoadingNotification).then(() => {
            setIsModelLoading(false);
        }).catch(() => {
            setIsModelLoading(false)
        });
    }

    useEffect(() => {
        setFinalCanvasBase64(demoResultImageUrl);
        setOriginalCanvasBase64(demoSourceImageUrl);

    }, []);

    // 处理触摸开始事件
    const handleTouchStart = (e: any) => {
        if (!containerRef.current) return;
        separator?.addEventListener('mousemove', handleTouchMove);
    };
    // 处理触摸移动事件
    const handleTouchMove = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = (containerRef.current as any).getBoundingClientRect();
        const containerLeft = rect.left;
        const containerWidth = (containerRef as any).current.offsetWidth;
        const deltaX = e.clientX - containerLeft;
        setSliderPosition(Math.min(Math.max(deltaX, 0), containerWidth));
    };
    // 处理触摸结束事件
    const handleTouchEnd = () => {
        separator?.removeEventListener('mousemove', handleTouchMove);
    };

    // 选择模型的类型，比如通用模型或者动画模型
    const selectModelStyle: MenuProps['onClick'] = (e) => {
        if (isModelLoading || isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }

        setModelSelectKey({
            key: Number(e.key),
            label: modelSelectInfo[Number(e.key)].label,
        });
        if (Number(e.key) >= 0 && Number(e.key) <= RemoveBgModelList.length)
            setModelIndex(ModelTypeConstant.REMOVE_BG, Number(e.key));
        else setModelIndex(ModelTypeConstant.REMOVE_BG, -1);
        setIsModelLoading(true);
        recreateInferenceSession(showAlert, showLoadingNotification).then(r => {
            setIsModelLoading(false);
        }).catch(() => {
            setIsModelLoading(false);
        });
    }

    const uploadFile = async (ev: any) => {
        if (!modelIsLoaded()) {
            showAlert({
                message: "Loading model and trying again later",
                type: "warning",
            })
            return;
        }
        if (isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }
        setIsLoading(false);//重置

        const file = ev.currentTarget.files?.[0];
        if (file) {
            setIsLoading(true);

            const imageUrl = URL.createObjectURL(file);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                // 图像加载完成，处理图像数据
                imageBgRemove(image, showAlert).then((res: any) => {
                    if (!res || !res.resultImgBase64) return;
                    // @ts-ignore
                    setFinalCanvasBase64(res.resultImgBase64);
                    setOriginalCanvasBase64(res.originImgBase64);
                    setIsLoading(false);
                    setIsAllowEdit(true);
                });
            };
            // uploadImage(file, showAlert, async (f) => {
            //     const {
            //         file: resizeFile,
            //         resultImageUrl: resultImage,
            //         sourceImageUrl: sourceImage
            //     } = await resizeImageFile(f, 320);
            //     setSourceImage(sourceImage);
            //     setResultImage(resultImage);
            // });
        }
    }

    const handleUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';// 重置文件输入值
            fileInputRef.current.click();
        }
    }

    const handleDownload = () => {

    }
    const handleRefresh = () => {
        setOriginalCanvasBase64(demoSourceImageUrl);
        setFinalCanvasBase64(demoResultImageUrl);

        setSliderPosition(sliderInitPosition);
        setIsLoading(false);
        setIsAllowEdit(false);
    }

    const handleStartEditor = () => {
        setIsShowEditor(true);
    }

    const downloadImage = async () => {
        const response = await fetch(finalCanvasBase64);
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        basicDownloadImage(fileUrl, `IMG_${Date.now()}.png`, false);
    };

    return (
        <div className="i-main-table">
            {/*编辑去除背景后的图像*/}
            {isShowEditor &&
                <ImageEditor
                    closeEditorTable={() => setIsShowEditor(false)}
                />
            }

            {/*展示图像去除背景后的效果*/}
            {!isShowEditor &&
                <div className="i-img-table-container">
                    <div className="i-img-table">
                        <div className="image-opt-items">
                            <div className="image-opt-items-left">
                                <Tooltip placement="bottom" title="change background">
                                    <div className="px-2 py-1 rounded-md text-sm font-medium
                                            hover:bg-gradient-to-r from-[#6C5CE7] to-[#575CE7]
                                            transition-colors">
                                        <PaintBucket className={`text-[${isLight ? 'black' : 'white'}]`} />
                                    </div>
                                </Tooltip>
                                <Tooltip placement="bottom" title="upload your image">
                                    <div onClick={handleUpload} className="px-2 py-1 rounded-md text-sm font-medium
                                            hover:bg-gradient-to-r from-[#6C5CE7] to-[#575CE7]
                                            transition-colors">
                                        <ImageUp className={`text-[${isLight ? 'black' : 'white'}]`} />
                                        <input
                                            // @ts-ignore
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={uploadFile}
                                            accept="image/png, image/jpeg, image/webp"
                                        />
                                    </div>
                                </Tooltip>
                            </div>

                            {isAllowEdit &&
                                <div className="image-opt-items-right">
                                    <Tooltip placement="bottom" title="download image">
                                        <div onClick={handleDownload} className="px-2 py-1 rounded-md text-sm font-medium
                                                hover:bg-gradient-to-r from-[#6C5CE7] to-[#575CE7]
                                                transition-colors">
                                            <ImageDown className={`text-[${isLight ? 'black' : 'white'}]`} />
                                        </div>
                                    </Tooltip>
                                    <Tooltip placement="bottom" title="refresh">
                                        <div onClick={handleRefresh} className="px-2 py-1 rounded-md text-sm font-medium
                                                hover:bg-gradient-to-r from-[#6C5CE7] to-[#575CE7]
                                                transition-colors">
                                            <RefreshCw className={`text-[${isLight ? 'black' : 'white'}]`} />
                                        </div>
                                    </Tooltip>
                                </div>
                            }
                        </div>

                        <div className="i-main-container">
                            <div
                                className="image-comparison-container"
                                // @ts-ignore
                                ref={containerRef}
                            >
                                <CubeGridLoader
                                    dotBackgroundColor="#3498db"
                                    isOpen={isLoading}
                                />
                                <img src={finalCanvasBase64} className="comparison-image" style={{
                                    height: imageHeight,
                                    maxWidth: maxWidth,
                                    maxHeight: maxHeight
                                }}/>
                                <div
                                    className="clip-item"
                                    style={{clipPath: `inset(0px 0px 0px ${sliderPosition}px)`}}
                                >
                                    <img src={originalCanvasBase64} className="comparison-image" style={{
                                        height: imageHeight,
                                        maxWidth: maxWidth,
                                        maxHeight: maxHeight
                                    }}/>
                                </div>

                                <div
                                    aria-orientation="horizontal"
                                    aria-valuenow={sliderPosition}
                                    className="slider-container"
                                    style={{left: `${sliderPosition}px`, transform: 'translate3d(-50%, 0px, 0px)'}}
                                >
                                    <div className="slider-handle-root">
                                        <div className="slider-handle-line"></div>
                                        <div
                                            ref={r => {
                                                if (r && !separator) {
                                                    setSeparator(r)
                                                }
                                            }}
                                            className="slider-handle-button"
                                            onMouseEnter={handleTouchStart}
                                            onMouseMove={handleTouchMove}
                                            onMouseLeave={handleTouchEnd}
                                        >
                                            <div className="slider-handle-arrow"></div>
                                            <div className="slider-handle-arrow reverse"></div>
                                        </div>
                                        <div className="slider-handle-line"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isAllowEdit &&
                            <div className="image-opt-items">
                                <div className="image-opt-items-left">
                                    <Tooltip placement="bottom" title="edit it">
                                        <div onClick={handleStartEditor} className="px-2 py-1 rounded-[25px] text-sm font-medium
                                                hover:bg-gradient-to-r from-[#6C5CE7] to-[#575CE7]
                                                transition-colors">
                                            <Brush className={`text-[${isLight ? 'black' : 'white'}]`} />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        }
                    </div>
                    <div className="right-opt-items">
                        <Dropdown.Button
                            icon={<DownOutlined/>}
                            menu={{
                                items: modelSelectItems,
                                onClick: selectModelStyle,
                            }}
                            style={{display: 'flex', justifyContent: 'center'}}
                        >
                            {modelSelectKey.label}
                        </Dropdown.Button>

                        <Tooltip placement="bottom" title="Up to 5000 x 5000 px">
                            <div className="vip-opt-button i-opt-button">
                                Download HD
                            </div>
                        </Tooltip>

                        <Tooltip placement="bottom" title="Up to original size">
                            <div className="ordinary-opt-button i-opt-button" onClick={downloadImage}>
                                Download Free
                            </div>
                        </Tooltip>
                    </div>
                </div>
            }
        </div>
    );
}
